import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/cn";

export type OrgDoCadastro = { id: string; name: string; atual: boolean };

type Props = {
  /** Organização atual primeiro (sempre marcada), depois as que a pessoa é proprietária. */
  organizacoes: OrgDoCadastro[];
  ativo: boolean;
  onAtivoChange: (v: boolean) => void;
  selecionadas: Set<string>;
  onToggle: (id: string) => void;
};

/**
 * O bloco "Cadastrar em todas as minhas organizações" do dialog de paciente. O padrão continua
 * todas; o botão expande a lista pra escolher em quais cadastrar. A organização atual fica
 * sempre marcada — o cadastro nasce nela.
 */
export function EscolherOrganizacoesDoCadastro({
  organizacoes,
  ativo,
  onAtivoChange,
  selecionadas,
  onToggle,
}: Props) {
  const [expandido, setExpandido] = useState(false);
  const total = organizacoes.length;
  const escolhidas = organizacoes.filter((o) => o.atual || selecionadas.has(o.id)).length;
  const titulo =
    !ativo || escolhidas === total
      ? "Cadastrar em todas as minhas organizações"
      : `Cadastrar em ${escolhidas} de ${total} organizações`;

  return (
    <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2.5 text-sm dark:border-border dark:bg-muted/30">
      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={ativo}
          onChange={(e) => onAtivoChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
        />
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-zinc-900 dark:text-foreground">{titulo}</span>
          <span className="block text-xs text-zinc-500 dark:text-muted-foreground">
            Cria o mesmo paciente nas {total} organizações das quais você é proprietário, não só na atual.
          </span>
        </span>
      </label>

      {ativo && (
        <>
          <button
            type="button"
            onClick={() => setExpandido((v) => !v)}
            className="-ml-1.5 mt-1 inline-flex h-9 items-center gap-1 rounded-md px-1.5 text-xs font-medium text-brand-700 transition-colors hover:text-brand-800 dark:text-primary"
          >
            {expandido ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Escolher em quais organizações
          </button>
          {expandido && (
            <ul className="mt-1 space-y-0.5">
              {organizacoes.map((o) => (
                <li key={o.id}>
                  <label
                    className={cn(
                      "flex h-10 items-center gap-2.5 rounded-md px-2",
                      o.atual ? "cursor-default opacity-80" : "cursor-pointer hover:bg-zinc-100 dark:hover:bg-muted",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={o.atual || selecionadas.has(o.id)}
                      disabled={o.atual}
                      onChange={() => onToggle(o.id)}
                      className="h-4 w-4 shrink-0 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-zinc-800 dark:text-foreground">
                      {o.name}
                    </span>
                    {o.atual && (
                      <span className="shrink-0 text-[10px] font-semibold uppercase text-zinc-400 dark:text-muted-foreground">
                        Atual
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
